import Joi from 'joi';
import api from '../api';

/**
 * Get airports
 * @param {object} queryParams - The query parameters.
 * @param {string} queryParams.search - The search query.
 * @param {number} queryParams.pageNumber - The page number.
 * @param {number} queryParams.pageSize - The page size.
 * @returns {Promise<any>} - The airports.
 */
export const getAirports = async (queryParams) => {
  const result = await api.get('/airports', { params: queryParams });

  try {
    const schema = Joi.object({
      total: Joi.number().required(),
      pageNumber: Joi.number().required(),
      data: Joi.array().items(
        Joi.object({
          code: Joi.string().required(),
          name: Joi.string().required(),
          city: Joi.string().allow(null, ''),
        }),
      ),
    }).required();

    await schema.validateAsync(result.data, { allowUnknown: true });
    return result.data;
  } catch (/** @type {any} */ error) {
    const errorObject = { error: `API response do not match get airports Schema : ${error.message}` };
    throw errorObject;
  }
};

/**
 * Get airport by ID
 * @param {string} airportId - The airport ID.
 * @returns {Promise<any>} - The airport.
 */
export const getAirport = async (airportId) => {
  const result = await api.get(`/airports/${airportId}`);

  try {
    const schema = Joi.object({
      code: Joi.string().required(),
      name: Joi.string().required(),
      city: Joi.string().allow(null, ''),
    }).required();

    await schema.validateAsync(result.data, { allowUnknown: true });
    return result.data;
  } catch (/** @type {any} */ error) {
    const errorObject = { error: `API response do not match get airport Schema : ${error.message}` };
    throw errorObject;
  }
};

/**
 * Create airport
 * @param {FullAirport} airport - The airport object.
 * @returns {Promise<{id: string}>} - The created airport ID.
 */
export const createAirport = async (airport) => {
  const result = await api.post('/airports', airport);
  try {
    const schema = Joi.object({
      id: Joi.number().required(),
    }).required();

    await schema.validateAsync(result.data, { allowUnknown: true });
    return result.data;
  } catch (/** @type {any} */ error) {
    const errorObject = { error: `API response do not match create airport Schema : ${error.message}` };
    throw errorObject;
  }
};

/**
 * Update airport
 * @param {FullAirport & {id: number}} airport - The airport object (must include `id`).
 * @returns {Promise<any>} - The updated airport.
 */
export const updateAirport = async (airport) => {
  const { id: airportId, ...rest } = airport;

  // Drop only undefined/null; keep "", false and 0 so updates can clear values
  const cleanBody = Object.fromEntries(
    Object.entries(rest).filter(([, val]) => val !== undefined && val !== null),
  );

  const result = await api.put(`/airports/${airportId}`, cleanBody);
  return result.status;
};

/**
 * Delete airport
 * @param {string} airportId The airport identifier.
 * @returns {Promise<void>} - Resolves when airport is deleted.
 */
export const deleteAirport = async (airportId) => {
  await api.delete(`/airports/${airportId}`);
};
